import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { CreditCard, ArrowRight, Loader2, Shield, CheckCircle2, Tag, X, User, Briefcase } from 'lucide-react';
import { trackCheckoutStarted, trackCouponApplied, trackRecCheckoutStarted } from '../utils/analytics';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function detectCurrency() {
  try {
    const lang = navigator.language || navigator.languages?.[0] || '';
    if (/^(nb|nn|no)/i.test(lang)) return 'nok';
  } catch { /* intentional: nok detection is optional */ }
  return 'usd';
}

const PayPalIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.645h6.563c2.175 0 3.74.58 4.65 1.724.426.535.698 1.145.811 1.814.118.696.12 1.544-.006 2.593l-.013.085v.754l.588.332a3.5 3.5 0 0 1 1.182 1.05c.398.567.652 1.27.756 2.088.107.84.072 1.833-.104 2.95a8.9 8.9 0 0 1-.93 2.85 5.7 5.7 0 0 1-1.502 1.826c-.604.476-1.32.827-2.128 1.044a9.9 9.9 0 0 1-2.653.33h-.63a1.9 1.9 0 0 0-1.184.413 1.9 1.9 0 0 0-.655 1.105l-.032.166-.547 3.464-.025.12a.2.2 0 0 1-.056.11.16.16 0 0 1-.103.04Z" fill="#253B80"/>
    <path d="M19.542 9.318a7 7 0 0 0-.086-.493c-.96-4.917-6.324-4.917-6.324-4.917H6.471L3.592 21.176a.42.42 0 0 0 .415.487h4.2l1.055-6.682a1.24 1.24 0 0 1 1.223-1.044h2.549c5.005 0 6.074-3.842 6.508-6.619Z" fill="#179BD7"/>
    <path d="M8.047 9.15a.77.77 0 0 1 .757-.644h4.783s2.912.048 4.192 1.98c-1.032-5.288-5.312-5.358-5.312-5.358H7.12a.77.77 0 0 0-.757.645L3.592 21.177a.42.42 0 0 0 .415.486h4.2l1.055-6.682L8.047 9.15Z" fill="#222D65"/>
  </svg>
);

export const ProductPurchaseDialog = ({ product, open, onOpenChange, initialLicenseType }) => {
  const { isShopifyBilled, shopifyBilling, isPro } = useAuth();
  const [currency] = useState(detectCurrency);
  // Use initialLicenseType > sessionStorage recommendation > 'personal'
  const getDefaultLicense = () => {
    if (initialLicenseType) return initialLicenseType;
    try {
      const rec = sessionStorage.getItem('lumera_license_recommendation');
      if (rec === 'commercial' || rec === 'personal') return rec;
    } catch { /* ignore */ }
    return 'personal';
  };
  const [licenseType, setLicenseType] = useState(getDefaultLicense);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [error, setError] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [showCoupon, setShowCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponResult, setCouponResult] = useState(null);
  const [couponError, setCouponError] = useState('');
  const checkoutInFlight = useRef(false);

  useEffect(() => {
    if (open) {
      if (initialLicenseType) {
        setLicenseType(initialLicenseType);
      } else {
        setLicenseType(getDefaultLicense());
      }
    }
  }, [open, initialLicenseType]);

  if (!product) return null;

  const hasDualPricing = product.personalPriceUsd != null && product.commercialPriceUsd != null;

  // Compute the correct price based on selected license
  const getPrice = () => {
    if (!hasDualPricing) {
      return currency === 'nok' ? product.priceNok : product.priceUsd;
    }
    if (licenseType === 'commercial') {
      return currency === 'nok' ? product.commercialPriceNok : product.commercialPriceUsd;
    }
    return currency === 'nok' ? product.personalPriceNok : product.personalPriceUsd;
  };

  const basePrice = getPrice();
  const finalPrice = couponResult ? couponResult.final_price : basePrice;
  const priceDisplay = currency === 'nok' ? `${finalPrice} kr` : `$${finalPrice}`;
  const originalDisplay = currency === 'nok' ? `${basePrice} kr` : `$${basePrice}`;
  const licenseLabel = licenseType === 'commercial' ? 'Commercial License' : 'Personal Use';

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    setCouponResult(null);
    try {
      const res = await axios.post(`${API}/coupons/validate`, {
        code: couponCode.trim(),
        product_slug: product.slug,
        amount: basePrice,
        currency,
      });
      setCouponResult(res.data);
      trackCouponApplied(product.slug, res.data.code, res.data.discount_amount);
    } catch (err) {
      setCouponError(err.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponResult(null);
    setCouponCode('');
    setCouponError('');
  };

  // Reset coupon when license type changes (different base price)
  const handleLicenseChange = (type) => {
    setLicenseType(type);
    handleRemoveCoupon();
  };

  const logConsent = async () => {
    try {
      await axios.post(`${API}/licenses/consent`, {
        consent_type: 'digital_product_purchase',
        session_id: product.slug + '_' + Date.now(),
      });
    } catch { /* consent logging is non-critical */ }
  };

  const handleStripeCheckout = async () => {
    if (checkoutInFlight.current) return;
    checkoutInFlight.current = true;
    await logConsent();
    setPaymentMethod('stripe');
    setCheckoutLoading(true);
    setError('');
    trackCheckoutStarted(product.slug, 'product', finalPrice, currency, 'stripe');
    try {
      const attr = JSON.parse(sessionStorage.getItem('rec_attribution') || 'null');
      if (attr && attr.slug === product.slug && (Date.now() - attr.clicked_at) < 1800000) {
        trackRecCheckoutStarted(product.slug, 'product', attr.placement, finalPrice, currency);
      }
    } catch { /* non-critical */ }
    try {
      const res = await axios.post(`${API}/purchases/checkout`, {
        product_slug: product.slug,
        origin_url: window.location.origin,
        currency,
        coupon_code: couponResult?.code || null,
        license_type: licenseType,
      });
      if (res.data.checkout_url) {
        sessionStorage.removeItem('lumera_license_recommendation');
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Checkout failed');
      setCheckoutLoading(false);
      setPaymentMethod(null);
      checkoutInFlight.current = false;
    }
  };

  const handlePayPalCheckout = async () => {
    if (checkoutInFlight.current) return;
    checkoutInFlight.current = true;
    await logConsent();
    setPaymentMethod('paypal');
    setCheckoutLoading(true);
    setError('');
    trackCheckoutStarted(product.slug, 'product', finalPrice, currency, 'paypal');
    try {
      const attr = JSON.parse(sessionStorage.getItem('rec_attribution') || 'null');
      if (attr && attr.slug === product.slug && (Date.now() - attr.clicked_at) < 1800000) {
        trackRecCheckoutStarted(product.slug, 'product', attr.placement, finalPrice, currency);
      }
    } catch { /* non-critical */ }
    try {
      const res = await axios.post(`${API}/purchases/paypal/create-order`, {
        product_slug: product.slug,
        currency,
        return_url: `${window.location.origin}/payment/success?type=product&provider=paypal&product=${product.slug}&license_type=${licenseType}`,
        cancel_url: `${window.location.origin}/?cancelled=${product.slug}`,
        coupon_code: couponResult?.code || null,
        license_type: licenseType,
      });
      if (res.data.approve_url) {
        sessionStorage.removeItem('lumera_license_recommendation');
        window.location.href = res.data.approve_url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'PayPal checkout failed');
      setCheckoutLoading(false);
      setPaymentMethod(null);
      checkoutInFlight.current = false;
    }
  };

  const handleClose = (o) => {
    if (!checkoutLoading) {
      onOpenChange(o);
      setPaymentMethod(null);
      checkoutInFlight.current = false;
      setError('');
      setConsentChecked(false);
      handleRemoveCoupon();
      setShowCoupon(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl border-stone-100 p-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="product-purchase-dialog">
        {isShopifyBilled ? (
          /* Shopify merchant — no off-platform checkout */
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-[#F0F7F0] flex items-center justify-center mx-auto mb-4">
              <Shield className="w-7 h-7 text-[#5E8E3E]" />
            </div>
            <h3 className="text-lg font-serif font-bold text-stone-800 mb-2">{product.title}</h3>
            {isPro ? (
              <>
                <p className="text-sm text-stone-500 mb-4">This product is included with your Shopify Pro plan. You have full access.</p>
                <button onClick={() => { handleClose(false); }} className="w-full bg-[#5E8E3E] text-white rounded-full py-3 font-semibold text-sm hover:bg-[#4E7E2E] transition-colors">
                  Continue to product
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-stone-500 mb-4">Upgrade to Lumera Pro through your Shopify admin to access all products with Commercial License rights.</p>
                <button onClick={() => window.open(`https://${shopifyBilling.shopDomain}/admin/settings/billing`, '_blank')} className="w-full bg-[#5E8E3E] text-white rounded-full py-3 font-semibold text-sm hover:bg-[#4E7E2E] transition-colors mb-2">
                  Upgrade in Shopify
                </button>
                <button onClick={() => handleClose(false)} className="w-full text-stone-400 text-xs py-2">
                  Cancel
                </button>
              </>
            )}
          </div>
        ) : (
        <>
        <div className="bg-stone-900 px-6 py-5 flex-shrink-0">
          <DialogHeader>
            <DialogTitle className="text-white font-serif text-xl">Buy {product.title}</DialogTitle>
            <DialogDescription className="text-stone-400 font-sans text-sm mt-1">
              One-time purchase &mdash; {priceDisplay}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6 space-y-3 overflow-y-auto flex-1">
          {/* License Type Selector */}
          {hasDualPricing && (
            <div data-testid="dialog-license-selector" className="space-y-2 mb-2">
              <button data-testid="dialog-select-personal" onClick={() => handleLicenseChange('personal')}
                className={`w-full text-left p-3.5 rounded-xl border-2 transition-all ${licenseType === 'personal' ? 'border-[#C49A6C] bg-[#F5F0EB]/50' : 'border-stone-200 hover:border-stone-300'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-stone-500" />
                    <span className="font-sans font-semibold text-stone-800 text-sm">Personal Use</span>
                  </div>
                  <span className="font-serif font-bold text-stone-900">
                    {currency === 'nok' ? `${product.personalPriceNok} kr` : `$${product.personalPriceUsd?.toFixed(2)}`}
                  </span>
                </div>
                <p className="text-[11px] font-sans text-stone-500 mt-1 ml-6">For your own personal use. No resale or commercial use.</p>
              </button>
              <button data-testid="dialog-select-commercial" onClick={() => handleLicenseChange('commercial')}
                className={`w-full text-left p-3.5 rounded-xl border-2 transition-all ${licenseType === 'commercial' ? 'border-[#C49A6C] bg-[#F5F0EB]/50' : 'border-stone-200 hover:border-stone-300'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-stone-500" />
                    <span className="font-sans font-semibold text-stone-800 text-sm">Commercial License</span>
                  </div>
                  <span className="font-serif font-bold text-stone-900">
                    {currency === 'nok' ? `${product.commercialPriceNok} kr` : `$${product.commercialPriceUsd?.toFixed(2)}`}
                  </span>
                </div>
                <p className="text-[11px] font-sans text-stone-500 mt-1 ml-6">For creators and businesses. Commercial License included.</p>
                <div className="flex items-center gap-1 mt-1 ml-6">
                  <Shield className="w-3 h-3 text-[#8B7B6B]" />
                  <span className="text-[10px] font-sans font-medium text-[#8B7B6B]">Commercial License included</span>
                </div>
              </button>
            </div>
          )}

          {/* Product info summary */}
          <div className="bg-stone-50 rounded-xl p-4 mb-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-sans font-semibold text-stone-800 text-sm">{product.title}</p>
                <p className="font-sans text-xs text-stone-500 mt-0.5">{product.pageCount} &middot; {licenseLabel}</p>
              </div>
              <div className="text-right">
                {couponResult ? (
                  <>
                    <p className="font-sans text-xs text-stone-400 line-through">{originalDisplay}</p>
                    <p className="font-serif font-bold text-[#8B7B6B] text-lg">{priceDisplay}</p>
                  </>
                ) : (
                  <p className="font-serif font-bold text-stone-900 text-lg">{priceDisplay}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#C49A6C]" />
              <span className="text-[11px] font-sans text-stone-500">Lifetime access after purchase</span>
            </div>
            {licenseType === 'commercial' && (
              <div className="flex items-center gap-2 mt-1.5">
                <Shield className="w-3.5 h-3.5 text-[#8B7B6B]" />
                <span className="text-[11px] font-sans text-[#8B7B6B]">Commercial License included</span>
              </div>
            )}
            {licenseType === 'personal' && (
              <div className="flex items-center gap-2 mt-1.5">
                <User className="w-3.5 h-3.5 text-stone-400" />
                <span className="text-[11px] font-sans text-stone-500">Personal use only — no commercial rights</span>
              </div>
            )}
          </div>

          {/* Coupon section */}
          {couponResult ? (
            <div data-testid="coupon-applied" className="flex items-center justify-between bg-[#F5F0EB] border border-[#EDE3D6] rounded-xl px-4 py-2.5">
              <div className="flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-[#8B7B6B]" />
                <span className="text-xs font-sans font-semibold text-[#8B7B6B]">{couponResult.code}</span>
                <span className="text-xs font-sans text-[#8B7B6B]">
                  -{currency === 'nok' ? `${couponResult.discount_amount} kr` : `$${couponResult.discount_amount}`}
                </span>
              </div>
              <button onClick={handleRemoveCoupon} className="text-stone-400 hover:text-stone-600">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : !showCoupon ? (
            <button data-testid="show-coupon-btn" onClick={() => setShowCoupon(true)}
              className="text-xs font-sans text-stone-400 hover:text-stone-600 transition-colors flex items-center gap-1.5">
              <Tag className="w-3 h-3" /> Have a discount code?
            </button>
          ) : (
            <div data-testid="coupon-input-section" className="flex gap-2">
              <input data-testid="coupon-input" type="text" value={couponCode}
                onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                placeholder="Enter code"
                className="flex-1 px-3 py-2 text-xs font-sans rounded-lg border border-stone-200 focus:border-stone-400 focus:outline-none bg-white"
                onKeyDown={(e) => { if (e.key === 'Enter') handleValidateCoupon(); }}
              />
              <button data-testid="apply-coupon-btn" onClick={handleValidateCoupon}
                disabled={couponLoading || !couponCode.trim()}
                className="px-3 py-2 text-xs font-sans font-semibold bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50 transition-colors">
                {couponLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
              </button>
            </div>
          )}
          {couponError && <p data-testid="coupon-error" className="text-xs font-sans text-red-500 -mt-1">{couponError}</p>}

          {error && (
            <div data-testid="purchase-error" className="bg-red-50 border border-red-200 text-red-700 text-sm font-sans rounded-xl px-4 py-3">
              {error}
            </div>
          )}

          {/* Consent */}
          <div data-testid="checkout-consent" className="bg-[#F7F2EC] border border-[#EDE3D6] rounded-xl p-4 space-y-2.5">
            <p className="text-[11px] font-semibold text-[#4A3525] tracking-wide uppercase">Digital Product &amp; Refund Policy</p>
            <p className="text-[11px] text-[#8A7D72] leading-relaxed">
              Due to the digital nature of this product and immediate access being provided after purchase, purchases are non-refundable once the product has been accessed, generated or downloaded, except where required by applicable law.
            </p>
            <label className="flex items-start gap-2.5 cursor-pointer group" data-testid="consent-label">
              <input data-testid="consent-checkbox" type="checkbox" checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-[#DDD4C8] text-[#4A3525] focus:ring-[#C4A98D] flex-shrink-0" />
              <span className="text-[11px] text-[#8A7D72] leading-relaxed">
                I request immediate access to this digital product. I understand that by receiving immediate access, I may lose my right of withdrawal. I have reviewed the{' '}
                <a href="/refund-policy" target="_blank" className="text-[#8C7A66] underline hover:text-[#4A3525]">Refund Policy</a>{' '}and{' '}
                <a href="/terms" target="_blank" className="text-[#8C7A66] underline hover:text-[#4A3525]">Terms of Service</a>.
              </span>
            </label>
          </div>

          {/* Stripe */}
          <button data-testid="purchase-stripe-btn" onClick={handleStripeCheckout}
            disabled={checkoutLoading || !consentChecked}
            className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200 text-left group ${
              paymentMethod === 'stripe' ? 'border-primary bg-primary/5' : 'border-stone-100 hover:border-stone-300 bg-white'
            } ${(checkoutLoading && paymentMethod !== 'stripe') || !consentChecked ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <div className="w-12 h-12 rounded-xl bg-[#635BFF]/10 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-6 h-6 text-[#635BFF]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-sans font-semibold text-stone-800 text-sm">Card</p>
              <p className="font-sans text-xs text-stone-500 mt-0.5">Visa, Mastercard, Amex &mdash; via Stripe</p>
            </div>
            {checkoutLoading && paymentMethod === 'stripe' ? (
              <Loader2 className="w-5 h-5 text-primary animate-spin flex-shrink-0" />
            ) : (
              <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-stone-600 transition-colors flex-shrink-0" />
            )}
          </button>

          {/* PayPal */}
          <button data-testid="purchase-paypal-btn" onClick={handlePayPalCheckout}
            disabled={checkoutLoading || !consentChecked}
            className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200 text-left group ${
              paymentMethod === 'paypal' ? 'border-primary bg-primary/5' : 'border-stone-100 hover:border-stone-300 bg-white'
            } ${(checkoutLoading && paymentMethod !== 'paypal') || !consentChecked ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <div className="w-12 h-12 rounded-xl bg-[#0070BA]/10 flex items-center justify-center flex-shrink-0">
              <PayPalIcon />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-sans font-semibold text-stone-800 text-sm">PayPal</p>
              <p className="font-sans text-xs text-stone-500 mt-0.5">Pay with PayPal balance or card</p>
            </div>
            {checkoutLoading && paymentMethod === 'paypal' ? (
              <Loader2 className="w-5 h-5 text-primary animate-spin flex-shrink-0" />
            ) : (
              <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-stone-600 transition-colors flex-shrink-0" />
            )}
          </button>

          <div className="flex items-center justify-center gap-2 pt-3 flex-wrap">
            <Shield className="w-3.5 h-3.5 text-stone-400" />
            <p className="text-[11px] font-sans text-stone-400">Secure payment</p>
            <span className="text-stone-300">&middot;</span>
            <a href="/license-terms" target="_blank" className="text-[11px] font-sans text-stone-400 hover:text-stone-600 underline">License Terms</a>
            <span className="text-stone-300">&middot;</span>
            <a href="/refund-policy" target="_blank" className="text-[11px] font-sans text-stone-400 hover:text-stone-600 underline">Refund Policy</a>
          </div>
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};
